// components/News.js
import React, { useState } from 'react';
import styled from 'styled-components';
import { FaTimes, FaCalendar, FaShare } from 'react-icons/fa';

const NewsSection = styled.section`
  padding: 4rem 2rem;
  max-width: 1200px;
  margin: 0 auto;

  @media (max-width: 768px) {
    padding: 2rem 1rem;
  }
`;

const NewsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin-top: 2rem;
`;

const NewsCard = styled.article`
  background-color: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease;

  &:hover {
    transform: translateY(-5px);
  }
`;

const NewsImage = styled.img`
  width: 100%;
  height: 200px;
  object-fit: cover;
`;

const NewsContent = styled.div`
  padding: 1.5rem;
`;

const NewsDate = styled.span`
  color: #666;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const NewsTitle = styled.h3`
  margin: 1rem 0;
  color: ${props => props.theme.colors.primary};
`;

const NewsExcerpt = styled.p`
  color: #666;
  margin-bottom: 1rem;
`;

const ReadMore = styled.button`
  color: ${props => props.theme.colors.secondary};
  font-weight: bold;
  text-decoration: none;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  &:hover {
    text-decoration: underline;
  }
`;

// Modal Styles
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 1rem;
  opacity: ${props => props.isOpen ? 1 : 0};
  visibility: ${props => props.isOpen ? 'visible' : 'hidden'};
  transition: all 0.3s ease;
`;

const ModalContent = styled.div`
  background-color: white;
  border-radius: 8px;
  max-width: 800px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  transform: ${props => props.isOpen ? 'translateY(0)' : 'translateY(-20px)'};
  transition: transform 0.3s ease;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
  }

  &::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 4px;
  }
`;

const ModalHeader = styled.div`
  position: sticky;
  top: 0;
  background-color: white;
  padding: 1.5rem;
  border-bottom: 1px solid #eee;
  display: flex;
  justify-content: space-between;
  align-items: start;
`;

const ModalBody = styled.div`
  padding: 1.5rem;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #666;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;

  &:hover {
    color: ${props => props.theme.colors.primary};
    transform: rotate(90deg);
  }
`;

const ModalImage = styled.img`
  width: 100%;
  height: 300px;
  object-fit: cover;
  border-radius: 8px;
  margin-bottom: 1.5rem;
`;

const ShareButton = styled.button`
  background-color: ${props => props.theme.colors.secondary};
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.3s ease;

  &:hover {
    opacity: 0.9;
    transform: translateY(-2px);
  }
`;

const News = () => {
  const [selectedNews, setSelectedNews] = useState(null);

  const newsItems = [
    {
      id: 1,
      title: 'Annual Parents Conference 2023',
      date: 'June 15, 2023',
      image: 'conference.jpg',
      excerpt: 'Join us for our annual conference focusing on modern parenting challenges.',
      content: `
        <p>We are excited to announce our Annual Parents Conference 2023, bringing together parents, educators, and experts to discuss the most pressing challenges in modern parenting and education.</p>
        
        <h4>Conference Highlights:</h4>
        <ul>
          <li>Keynote speakers from leading educational institutions</li>
          <li>Interactive workshops on digital parenting</li>
          <li>Panel discussions on mental health and wellbeing</li>
          <li>Networking opportunities with other parents</li>
        </ul>

        <p>The conference will be held at the Nairobi International Convention Centre on June 15th, 2023. Early bird registration is now open with special discounts for NPA members.</p>
      `
    },
    {
      id: 2,
      title: "Empowering Families Through Parent Workshops",
      date: "October 4, 2023",
      image: "workshop.jpg",
      excerpt: "Discover practical strategies and resources to support families and enhance parenting skills.",
      content: `
        <p>Join us for a series of engaging workshops designed to empower parents with the knowledge and tools they need to support their children's development. From understanding child psychology to managing screen time, these workshops cover a wide range of topics to help parents navigate the challenges of modern parenting.</p>
        <h4>Workshop Highlights:</h4>
        <ul>
          <li>Understanding Child Development and Psychology</li>
          <li>Gentle Parenting Approach</li>
          <li>Teaching Life Skills to Kids</li>
          <li>Managing Parent Burnout</li>
          <li>Anger Management for Kids and Parents</li>
          <li>Effective Communication with Teens</li>
          <li>Healthy Habits and Nutrition</li>
        </ul>
        <p>These workshops provide a supportive environment for parents to share experiences and learn from experts in the field.</p>
      `
    },
    {
      id: 3,
      title: "Dual Training Policy for TVETs Approved",
      date: "January 21, 2025",
      image: "policy.jpg",
      excerpt: "The new policy aims to integrate classroom instruction with hands-on industry training to address the skills mismatch in the labor market.",
      content: `
        <p>The Cabinet has approved the implementation of the Dual Training Policy to strengthen technical and vocational education and training in Kenya. This policy aims to address the skills mismatch in the labor market by integrating classroom instruction with hands-on industry training. The approach ensures that students acquire both theoretical knowledge and relevant work experience, making them job-ready upon graduation. Under the Dual Training model, trainees will spend 50 to 70 percent of their training in industry and the rest in institutions.</p>
      `
    },    
    // Add more news items here
    // {
    //   id: number,
    //   title: string,
    //   date: string,
    //   image: string,
    //   excerpt: string,
    //   content: string // Can include HTML markup
    // },
  ];

  const handleShare = (newsItem) => {
    if (navigator.share) {
      navigator.share({
        title: newsItem.title,
        text: newsItem.excerpt,
        url: window.location.href,
      })
      .catch((error) => console.log('Error sharing:', error));
    }
  };

  return (
    <NewsSection>
      <h2>Latest News</h2>
      <NewsGrid>
        {newsItems.map((item) => (
          <NewsCard key={item.id}>
            <NewsImage src={item.image} alt={item.title} />
            <NewsContent>
              <NewsDate>
                <FaCalendar />
                {item.date}
              </NewsDate>
              <NewsTitle>{item.title}</NewsTitle>
              <NewsExcerpt>{item.excerpt}</NewsExcerpt>
              <ReadMore onClick={() => setSelectedNews(item)}>
                Read More →
              </ReadMore>
            </NewsContent>
          </NewsCard>
        ))}
      </NewsGrid>

      <ModalOverlay isOpen={!!selectedNews} onClick={() => setSelectedNews(null)}>
        <ModalContent
          isOpen={!!selectedNews}
          onClick={(e) => e.stopPropagation()}
        >
          {selectedNews && (
            <>
              <ModalHeader>
                <div>
                  <h2>{selectedNews.title}</h2>
                  <NewsDate>
                    <FaCalendar />
                    {selectedNews.date}
                  </NewsDate>
                </div>
                <div style={{ display: 'flex', gap: '1rem' }}>
                  <ShareButton onClick={() => handleShare(selectedNews)}>
                    <FaShare />
                    Share
                  </ShareButton>
                  <CloseButton onClick={() => setSelectedNews(null)}>
                    <FaTimes />
                  </CloseButton>
                </div>
              </ModalHeader>
              <ModalBody>
                <ModalImage src={selectedNews.image} alt={selectedNews.title} />
                <div dangerouslySetInnerHTML={{ __html: selectedNews.content }} />
              </ModalBody>
            </>
          )}
        </ModalContent>
      </ModalOverlay>
    </NewsSection>
  );
};

export default News;